import React, { useState, useEffect } from "react";
import { Form } from "react-router-dom";
import AccountSectionWrapper from "./AccountSectionWrapper";
import ModalNew from "../popup/Modal";
import ApiResponseModal from "../popup/ApiResponseModal";
import { PopupHeader, PopupContent } from "../popup/common";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import {
  useGetAccountQuery,
  useUpdateAccountSettingsMutation,
  useUpdatePreferencesMutation,
} from "../../../api/account";
import TOTPSetupModal from "../popup/TOTPSetupModal";
import MfaMethodCard from "../cards/mfa/MfaMethodCard";
import PasskeysSection from "./PasskeysSection";

const MFA_CATALOG = {
  email: {
    id: "email",
    name: "Email",
    description:
      "Receive a 6-digit verification code sent to your registered email.",
  },
  totp: {
    id: "totp",
    name: "Authenticator App",
    description:
      "Use codes generated by apps like Google Authenticator or 1Password.",
  },
};

const SecurityContent = () => {
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isTotpSetupOpen, setIsTotpSetupOpen] = useState(false);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [is2faModalOpen, setIs2faModalOpen] = useState(false);

  const {
    data: accountInfo,
    isLoading: isAccountLoading,
    refetch,
  } = useGetAccountQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const [updateAccountSettings] = useUpdateAccountSettingsMutation();
  const [updatePreferences] = useUpdatePreferencesMutation();

  const hasUsablePassword = accountInfo?.has_usable_password !== false;

  const mfaStatus = accountInfo?.mfa_status || {
    supported_methods: ["email"],
    enrolled_methods: ["email"],
    preferred_method: "email",
  };

  const supported2faMethods = (mfaStatus.supported_methods || [])
    .map((methodId) => MFA_CATALOG[methodId])
    .filter(Boolean);

  const [selectedPreference, setSelectedPreference] = useState(
    mfaStatus.preferred_method,
  );

  useEffect(() => {
    if (mfaStatus?.preferred_method) {
      setSelectedPreference(mfaStatus.preferred_method);
    }
  }, [mfaStatus?.preferred_method]);

  const [passwordData, setPasswordData] = useState({
    password: "",
    new_password: "",
    confirm_new_password: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    password: false,
    new_password: false,
    confirm_new_password: false,
  });

  useEffect(() => {
    setIsInitialLoading(false);
  }, []);

  const handleEditClick = () => setIsPasswordModalOpen(true);

  const handleSaveChanges = async (event) => {
    event.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_new_password) {
      alert("New passwords do not match");
      return;
    }

    setIsPasswordModalOpen(false);
    setIsResultModalOpen(true);
    setIsLoading(true);
    setResultMessage("Updating password...");

    try {
      const result = await updateAccountSettings({
        setting: "Credentials",
        values: passwordData,
      });

      if (result.error) {
        setIsSuccess(false);
        setResultMessage(
          result.error === 400
            ? "Current password is incorrect. Please try again."
            : "We couldn't update your information. Please try again.",
        );
        setIsLoading(false);
        return;
      }

      setPasswordData({
        password: "",
        new_password: "",
        confirm_new_password: "",
      });
      setIsSuccess(true);
      setResultMessage(
        hasUsablePassword
          ? "Your information has been updated successfully."
          : "Your password has been set successfully.",
      );
      setIsLoading(false);
    } catch (error) {
      setIsSuccess(false);
      setResultMessage(
        "We couldn't update your information. Please try again.",
      );
      setIsLoading(false);
    }
  };

  const handleEdit2faClick = () => {
    setSelectedPreference(mfaStatus.preferred_method);
    setIs2faModalOpen(true);
  };

  const handleEnableMethod = (methodId) => {
    if (methodId === "totp") {
      setIsTotpSetupOpen(true);
    }
  };

  const handleTotpSetupComplete = async () => {
    setIsTotpSetupOpen(false);
    await refetch();
    setResultMessage("Authenticator app enabled successfully!");
    setIsSuccess(true);
    setIsResultModalOpen(true);
  };

  const handleSave2faPreferences = async (e) => {
    e.preventDefault();
    setIs2faModalOpen(false);
    setIsResultModalOpen(true);
    setIsLoading(true);
    setResultMessage("Updating 2FA preference...");

    try {
      const result = await updatePreferences({
        section: "security",
        preferences: { mfa_type: selectedPreference },
      });

      if (result.error) {
        setIsSuccess(false);
        setResultMessage("Failed to update 2FA preference. Please try again.");
      } else {
        await refetch();
        setIsSuccess(true);
        setResultMessage(
          "Two-factor authentication preference updated successfully.",
        );
      }
    } catch (error) {
      setIsSuccess(false);
      setResultMessage("We couldn't update your preference. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    setResultMessage("");
    setIsLoading(false);
    setIsSuccess(false);
  };

  const handleInputChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  if (isInitialLoading || isAccountLoading) {
    return <LoadingPlaceholder rows={1} type="card" />;
  }

  return (
    <>
      {/* SECTION 1: PASSWORD MANAGEMENT */}
      <AccountSectionWrapper
        title={hasUsablePassword ? "Change Password" : "Set Password"}
        buttonText={hasUsablePassword ? "Change password" : "Set Password"}
        onButtonClick={handleEditClick}
      >
        <div className="security-content">
          <div className="form-field">
            <div className="form-input-representation">
              <p>{hasUsablePassword ? "••••••••" : "No password set"}</p>
            </div>
          </div>
        </div>
      </AccountSectionWrapper>

      {/* SECTION 2: TWO-FACTOR AUTHENTICATION (2FA) */}
      <AccountSectionWrapper
        title="Two-factor authentication"
        buttonText="Manage 2FA"
        onButtonClick={handleEdit2faClick}
      >
        <div className="security-content">
          <div
            className="mfa-methods-summary"
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {supported2faMethods.map((method) => {
              const isEnrolled = (mfaStatus.enrolled_methods || []).includes(
                method.id,
              );
              const isPreferred = mfaStatus.preferred_method === method.id;

              return (
                <MfaMethodCard
                  key={method.id}
                  method={method}
                  isEnrolled={isEnrolled}
                  isPreferred={isPreferred}
                  onActivate={handleEnableMethod}
                  variant="summary"
                />
              );
            })}
          </div>
        </div>
      </AccountSectionWrapper>

      <PasskeysSection />

      {/* Password Modal */}
      {isPasswordModalOpen && (
        <ModalNew className="content-modal">
          <PopupHeader
            title={hasUsablePassword ? "Change Password" : "Set Password"}
            setOpenState={setIsPasswordModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/key_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <Form className="modal-form" onSubmit={handleSaveChanges}>
              {hasUsablePassword && (
                <div className="form-field">
                  <label className="field-label">Current Password</label>
                  <div className="password-input-container">
                    <input
                      type={showPasswords.password ? "text" : "password"}
                      name="password"
                      value={passwordData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      className="form-input"
                      placeholder="Enter your current password"
                      required
                    />
                    <img
                      src={
                        showPasswords.password
                          ? "/images/icons/eye_open_blue.svg"
                          : "/images/icons/eye_closed_blue.svg"
                      }
                      alt="Toggle password visibility"
                      className="password-toggle-icon"
                      onClick={() => togglePasswordVisibility("password")}
                    />
                  </div>
                </div>
              )}

              <div className="form-field">
                <label className="field-label">New Password</label>
                <div className="password-input-container">
                  <input
                    type={showPasswords.new_password ? "text" : "password"}
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={(e) =>
                      handleInputChange("new_password", e.target.value)
                    }
                    className="form-input"
                    placeholder="Enter your new password"
                    required
                  />
                  <img
                    src={
                      showPasswords.new_password
                        ? "/images/icons/eye_open_blue.svg"
                        : "/images/icons/eye_closed_blue.svg"
                    }
                    alt="Toggle password visibility"
                    className="password-toggle-icon"
                    onClick={() => togglePasswordVisibility("new_password")}
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="field-label">Confirm New Password</label>
                <div className="password-input-container">
                  <input
                    type={
                      showPasswords.confirm_new_password ? "text" : "password"
                    }
                    name="confirm_new_password"
                    value={passwordData.confirm_new_password}
                    onChange={(e) =>
                      handleInputChange("confirm_new_password", e.target.value)
                    }
                    className="form-input"
                    placeholder="Confirm your new password"
                    required
                  />
                  <img
                    src={
                      showPasswords.confirm_new_password
                        ? "/images/icons/eye_open_blue.svg"
                        : "/images/icons/eye_closed_blue.svg"
                    }
                    alt="Toggle password visibility"
                    className="password-toggle-icon"
                    onClick={() =>
                      togglePasswordVisibility("confirm_new_password")
                    }
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="submit"
                  className="cta-button primary short text-center"
                >
                  Save Changes
                </button>
              </div>
            </Form>
          </PopupContent>
        </ModalNew>
      )}

      {/* 2FA Preferences Modal */}
      {is2faModalOpen && (
        <ModalNew className="content-modal">
          <PopupHeader
            title="Manage Two-Factor Authentication"
            setOpenState={setIs2faModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/key_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <Form className="modal-form" onSubmit={handleSave2faPreferences}>
              <p className="description" style={{ marginBottom: "16px" }}>
                Select your preferred verification method for signing in.
              </p>

              <div
                className="mfa-methods-list"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {supported2faMethods.map((method) => {
                  const isEnrolled = (
                    mfaStatus.enrolled_methods || []
                  ).includes(method.id);
                  const isSelected = selectedPreference === method.id;

                  return (
                    <MfaMethodCard
                      key={method.id}
                      method={method}
                      isEnrolled={isEnrolled}
                      isSelected={isSelected}
                      onSelect={setSelectedPreference}
                      variant="modal"
                    />
                  );
                })}
              </div>

              <div className="modal-actions" style={{ marginTop: "20px" }}>
                <button
                  type="submit"
                  className="cta-button primary short text-center"
                  disabled={
                    selectedPreference === mfaStatus.preferred_method ||
                    !(mfaStatus.enrolled_methods || []).includes(
                      selectedPreference,
                    )
                  }
                >
                  Save Preference
                </button>
              </div>
            </Form>
          </PopupContent>
        </ModalNew>
      )}

      {/* Result Modal */}
      <ApiResponseModal
        isOpen={isResultModalOpen}
        isLoading={isLoading}
        isSuccess={isSuccess}
        message={resultMessage}
        onClose={handleCloseResultModal}
      />

      {isTotpSetupOpen && (
        <ModalNew className="content-modal">
          <PopupHeader
            title="Set up Authenticator App"
            setOpenState={setIsTotpSetupOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/key_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <TOTPSetupModal onSetupComplete={handleTotpSetupComplete} />
          </PopupContent>
        </ModalNew>
      )}
    </>
  );
};

export default SecurityContent;
